import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HardDrive, Play, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function AdminMigrateImages() {
  const [counter, setCounter] = useState(null);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);

  const loadCounter = useCallback(async () => {
    try {
      const r = await api.get("/admin/migrate-jobs/base64-count");
      setCounter(r.data);
    } catch {
      // ignore transient errors
    }
  }, []);

  const loadJob = useCallback(async () => {
    try {
      const r = await api.get("/admin/migrate-jobs/latest");
      setJob(r.data && r.data.status !== "none" ? r.data : null);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadCounter();
    loadJob();
  }, [loadCounter, loadJob]);

  // Poll while job is active
  useEffect(() => {
    if (!job || (job.status !== "queued" && job.status !== "running")) return;
    const iv = setInterval(() => {
      loadJob();
      loadCounter();
    }, 2000);
    return () => clearInterval(iv);
  }, [job, loadJob, loadCounter]);

  const startMigration = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const r = await api.post("/admin/recipes/migrate-images-to-storage");
      if (r.data.already_running) {
        toast.info("Une migration est déjà en cours.");
      } else {
        toast.success("Migration lancée.");
      }
      await loadJob();
    } catch (e) {
      toast.error("Échec du lancement.");
    } finally {
      setStarting(false);
    }
  };

  const isRunning = job && (job.status === "queued" || job.status === "running");
  const progress = job && job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;
  const mbFreed = job?.bytes_freed ? (job.bytes_freed / (1024 * 1024)).toFixed(2) : "0";

  return (
    <div className="space-y-8" data-testid="admin-migrate-images">
      <div>
        <p className="overline mb-2">Maintenance</p>
        <h1 className="font-serif text-4xl mb-2">Migration des images vers Object Storage</h1>
        <p className="text-sm text-[hsl(var(--text-secondary))] max-w-2xl">
          Déplace les images base64 stockées en base de données vers l'Object Storage Emergent.
          Réduit la taille de MongoDB et améliore les performances de l'API. Les URLs publiques
          continuent de fonctionner (l'endpoint <code>/api/recipes/&#123;slug&#125;/image</code> gère
          les deux formats).
        </p>
      </div>

      {/* Counter card */}
      <div className="border border-[hsl(var(--border))] rounded-lg p-6 bg-[hsl(var(--bg-subtle))] flex items-center gap-6" data-testid="migration-counter">
        <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center border border-[hsl(var(--border))]">
          <HardDrive className="w-6 h-6 text-[hsl(var(--brand-primary))]" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <p className="text-3xl font-serif text-[hsl(var(--text-primary))]">
            {counter?.base64_count ?? "…"}
            <span className="text-base text-[hsl(var(--text-muted))] ml-2">
              / {counter?.total_recipes ?? "?"} recettes
            </span>
          </p>
          <p className="text-sm text-[hsl(var(--text-secondary))] mt-1">
            Images encore stockées en base64 (à migrer)
          </p>
        </div>
        <button
          data-testid="start-migration-btn"
          onClick={startMigration}
          disabled={starting || isRunning || (counter?.base64_count ?? 0) === 0}
          className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {starting || isRunning ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              En cours...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" strokeWidth={1.5} />
              Lancer la migration
            </>
          )}
        </button>
      </div>

      {/* Job status */}
      {job && (
        <div className="border border-[hsl(var(--border))] rounded-lg p-6" data-testid="migration-job-status">
          <div className="flex items-center justify-between mb-4">
            <p className="overline">Dernier job</p>
            <span
              className={`text-xs px-2 py-1 rounded-sm font-medium ${
                job.status === "done"
                  ? "bg-green-100 text-green-800"
                  : job.status === "failed"
                  ? "bg-red-100 text-red-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {job.status}
            </span>
          </div>

          {isRunning && (
            <>
              <p className="text-sm text-[hsl(var(--text-secondary))] mb-2">
                {job.current_index} / {job.total} — {job.current || "..."}
              </p>
              <div className="h-2 bg-[hsl(var(--bg-subtle))] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[hsl(var(--brand-primary))] transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
            <div>
              <p className="text-[hsl(var(--text-muted))] text-xs mb-1">Traitées</p>
              <p className="font-serif text-2xl">{job.processed}</p>
            </div>
            <div>
              <p className="text-[hsl(var(--text-muted))] text-xs mb-1">Succès</p>
              <p className="font-serif text-2xl text-green-700">{job.success_count}</p>
            </div>
            <div>
              <p className="text-[hsl(var(--text-muted))] text-xs mb-1">Erreurs</p>
              <p className="font-serif text-2xl text-red-700">{job.error_count}</p>
            </div>
            <div>
              <p className="text-[hsl(var(--text-muted))] text-xs mb-1">Libéré</p>
              <p className="font-serif text-2xl">{mbFreed} <span className="text-sm">MB</span></p>
            </div>
          </div>

          {job.status === "done" && (
            <p className="mt-4 text-sm text-green-700 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Migration terminée avec succès.
            </p>
          )}

          {job.status === "failed" && job.fatal_error && (
            <div className="mt-4 p-3 border border-red-200 bg-red-50 rounded-sm text-sm text-red-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <span>{job.fatal_error}</span>
            </div>
          )}

          {job.errors && job.errors.length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-[hsl(var(--text-muted))] mb-2">Erreurs par recette :</p>
              <ul className="text-xs space-y-1 max-h-40 overflow-y-auto border border-[hsl(var(--border))] rounded-sm p-2">
                {job.errors.map((e, i) => (
                  <li key={i}>
                    <span className="font-medium">{e.title}</span> — {e.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
